import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  ConfirmDialog,
  DataTable,
  type FilterDef,
  DetailPanel,
  EmptyState,
  ErrorState,
  type FilterOption,
  Inline,
  InlineMessage,
  Input,
  ListView,
  PageContent,
  PageHeader,
  RecordFormDialog,
  type RecordFormField,
  Spinner,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  Textarea,
  TextLink,
} from '@olytic/foundation';
import { Handshake, History } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useDocumentTitle } from '../documentTitle';
import { SETUP_ROUTES } from '../navigation';
import { formatDate, formatDateTime } from '../objects/format';
import { recordPath } from '../objects/routes';
import { PartnerReferrals } from './PartnerReferrals';
import {
  deactivatePartner,
  declinePartner,
  formatReferralRate,
  getPartner,
  invitePartner,
  listPartners,
  listReferralRateHistory,
  NOTE_MAX,
  NOTE_TOO_LONG,
  PARTNER_REFERRED,
  PARTNER_SORT_REFERRALS,
  PARTNER_STATUSES,
  type PartnerAction,
  type PartnerCounts,
  type PartnerDetail,
  type PartnerList,
  type PartnerListRow,
  type PartnerReferred,
  type PartnerSort,
  type PartnerStatus,
  type PartnerUser,
  PARTNERS_PAGE_SIZE,
  parseReferralRate,
  REFERRAL_RATE_INVALID,
  REFERRAL_RATE_MAX,
  type ReferralRateChange,
  searchAccounts,
  searchPeople,
  setOlyticContact,
  setReferralRate,
  validateInvite,
  verifyPartner,
} from './partnersApi';

/**
 * Setup → Partners (MOTHER-117): who applied to the partner portal, and where each
 * application stands. Staff verify, decline or deactivate a partner from the detail page,
 * and invite one from the list (MOTHER-118); the detail page lists what the partner has
 * referred (MOTHER-123, PartnerReferrals.tsx), and staff change their Olytic contact there
 * (MOTHER-137), and an Admin their referral rate, with its history (MOTHER-144). The fetcher
 * and the person's role are passed in (App.tsx gives the app's authenticatedFetch and the
 * signed-in role), so tests can stub them.
 */

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** A tab: a status, or every partner. */
export type PartnersTab = 'all' | PartnerStatus;

/** The tabs, in order. The list opens on Pending: what staff have to act on. */
export const PARTNER_TABS: readonly { key: PartnersTab; label: string; empty: string }[] = [
  { key: 'all', label: 'All', empty: 'No partner applications.' },
  { key: 'pending', label: 'Pending', empty: 'No pending applications.' },
  { key: 'verified', label: 'Verified', empty: 'No verified partners.' },
  { key: 'declined', label: 'Declined', empty: 'No declined applications.' },
  { key: 'deactivated', label: 'Deactivated', empty: 'No deactivated partners.' },
];
export const DEFAULT_PARTNERS_TAB: PartnersTab = 'pending';

/** How long search waits after the last keystroke before it asks the server. */
export const SEARCH_PAUSE_MS = 300;

const STATUS_TONE: Record<PartnerStatus, 'warning' | 'success' | 'error' | 'neutral'> = {
  pending: 'warning',
  verified: 'success',
  declined: 'error',
  deactivated: 'neutral',
};

/**
 * The Referrals filter (MOTHER-135). ListView adds the no-filter choice itself; it reads
 * "All", which sends no `referred`.
 */
const REFERRED_FILTER: FilterDef = {
  key: 'referred',
  label: 'Referrals',
  inline: true,
  options: [
    { value: 'yes', label: 'Referred' },
    { value: 'no', label: 'Never referred' },
  ],
};

/** What an empty list says while the Referrals filter is set, whatever the tab or search. */
const NO_FILTER_MATCH = 'No partners match these filters.';

/** The column whose header sorts the list by referrals. */
const REFERRALS_COLUMN = 'referralCount';

const isTab = (value: string | null): value is PartnersTab => PARTNER_TABS.some((t) => t.key === value);
const isReferred = (value: string | null): value is PartnerReferred => (PARTNER_REFERRED as readonly (string | null)[]).includes(value);
const statusLabel = (status: string): string => PARTNER_TABS.find((t) => t.key === status)?.label ?? status;
const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');
const fullName = (p: PartnerUser): string => [p.firstName, p.lastName].filter((part) => part.trim() !== '').join(' ');

/** What the list shows: a tab, a search, the Referrals filter and the order (null: the defaults). */
export interface PartnersListParams {
  tab: PartnersTab;
  q: string;
  referred: PartnerReferred | null;
  sort: PartnerSort | null;
}

/** The list's query string: `status=verified&q=acme&referred=yes&sort=referrals`, each default left out but `status`. */
function listSearch({ tab, q, referred, sort }: PartnersListParams): URLSearchParams {
  const params = new URLSearchParams({ status: tab });
  if (q.trim() !== '') params.set('q', q.trim());
  if (referred !== null) params.set('referred', referred);
  if (sort !== null) params.set('sort', sort);
  return params;
}

/** The list's address: `/setup/partners?status=pending&q=acme&referred=yes&sort=referrals`. */
export function partnersListPath(tab: PartnersTab, q: string, referred: PartnerReferred | null = null, sort: PartnerSort | null = null): string {
  return `${SETUP_ROUTES.partners}?${listSearch({ tab, q, referred, sort }).toString()}`;
}

export const partnerPath = (id: string): string => `${SETUP_ROUTES.partners}/${encodeURIComponent(id)}`;

/**
 * What a list address asks for: Pending, no search, every partner and newest first for
 * whatever it does not name. An unknown `status`, `referred` or `sort` is ignored.
 */
export function readListParams(params: URLSearchParams): PartnersListParams {
  const status = params.get('status');
  const referred = params.get('referred');
  return {
    tab: isTab(status) ? status : DEFAULT_PARTNERS_TAB,
    q: (params.get('q') ?? '').trim(),
    referred: isReferred(referred) ? referred : null,
    sort: params.get('sort') === PARTNER_SORT_REFERRALS ? PARTNER_SORT_REFERRALS : null,
  };
}

/** "Pending", with its count once the server has sent the counts. `All` is the sum of the four. */
export function tabCount(tab: PartnersTab, counts: PartnerCounts | null): number | null {
  if (counts === null) return null;
  return tab === 'all' ? PARTNER_STATUSES.reduce((sum, s) => sum + counts[s], 0) : counts[tab];
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const tone = (PARTNER_STATUSES as readonly string[]).includes(status) ? STATUS_TONE[status as PartnerStatus] : 'neutral';
  return (
    <Badge tone={tone} size="sm">
      {statusLabel(status)}
    </Badge>
  );
}

/**
 * The list's columns. Referrals and Last Referral (MOTHER-129) follow Status: on a phone's
 * card the count sits beside the status, and the date is left out.
 */
export const COLUMNS: ColumnDef<PartnerListRow>[] = [
  { key: 'name', label: 'Name', primary: true, get: fullName },
  { key: 'companyName', label: 'Company', get: (row) => row.companyName },
  { key: 'email', label: 'Email', get: (row) => row.email },
  { key: 'status', label: 'Status', mobileHeader: true, get: (row) => row.status, render: (row) => <StatusBadge status={row.status} /> },
  // Its header sorts by referrals, most first (MOTHER-135): the only sortable column.
  { key: REFERRALS_COLUMN, label: 'Referrals', align: 'right', nowrap: true, mobileHeader: true, sortable: true, get: (row) => row.referralCount, render: (row) => String(row.referralCount) },
  { key: 'lastReferralAt', label: 'Last Referral', nowrap: true, hideOnMobile: true, get: (row) => row.lastReferralAt, render: (row) => formatDate(row.lastReferralAt) },
  { key: 'createdAt', label: 'Applied', nowrap: true, get: (row) => row.createdAt, render: (row) => formatDate(row.createdAt) },
  { key: 'decidedAt', label: 'Decided', nowrap: true, get: (row) => row.decidedAt, render: (row) => formatDate(row.decidedAt) },
];

type ListLoad = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; list: PartnerList };

/**
 * What a history entry into the detail page carries: where its back link returns (the
 * list's own address), and a notice to show once, e.g. `Invited pat@acme.example.test.`
 */
interface FromList {
  partnersList?: string;
  notice?: string;
}

/* ── Invite Partner ────────────────────────────────────────────── */

/** The Invite Partner form: the route's four fields, all required. */
function inviteFields(loadAccounts: (query: string) => Promise<FilterOption[]>): RecordFormField[] {
  return [
    { key: 'email', label: 'Email', required: true, editor: { kind: 'text', maxLength: 300 } },
    { key: 'firstName', label: 'First Name', required: true, editor: { kind: 'text', maxLength: 100 } },
    { key: 'lastName', label: 'Last Name', required: true, editor: { kind: 'text', maxLength: 100 } },
    { key: 'accountId', label: 'Account', required: true, editor: { kind: 'lookup', loadOptions: loadAccounts } },
  ];
}

/* ── /setup/partners ───────────────────────────────────────────── */

export function SetupPartnersPage({ fetcher }: { fetcher: Fetch }): JSX.Element {
  useDocumentTitle('Partners');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { tab, q, referred, sort } = readListParams(params);
  const [searchText, setSearchText] = useState(q);
  const [load, setLoad] = useState<ListLoad>({ state: 'loading' });
  const [counts, setCounts] = useState<PartnerCounts | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The page's offset belongs to one tab, search, filter and order; another starts at the first
  // page. The offset is dropped, not kept aside, when the choice changes: returning to an earlier
  // choice (All again, or the sort cleared) starts it on its first page too, not the one left.
  const listKey = `${tab}\n${q}\n${referred ?? ''}\n${sort ?? ''}`;
  const [paging, setPaging] = useState({ key: listKey, offset: 0 });
  if (paging.key !== listKey) setPaging({ key: listKey, offset: 0 });
  const offset = paging.key === listKey ? paging.offset : 0;
  const pause = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [inviting, setInviting] = useState(false);
  const loadAccounts = useCallback((query: string) => searchAccounts(fetcher, query), [fetcher]);
  const fieldsForInvite = useMemo(() => inviteFields(loadAccounts), [loadAccounts]);

  // The address changed underneath the box (back, a link): show its search.
  useEffect(() => setSearchText(q), [q]);
  useEffect(() => () => clearTimeout(pause.current), []);

  useEffect(() => {
    let live = true;
    setLoad({ state: 'loading' });
    listPartners(fetcher, { status: tab === 'all' ? '' : tab, q, referred, sort, limit: PARTNERS_PAGE_SIZE, offset }).then(
      (list) => {
        if (!live) return;
        setLoad({ state: 'ready', list });
        if (referred === null) setCounts(list.counts);
      },
      (error: unknown) => live && setLoad({ state: 'error', message: errorText(error) }),
    );
    // The route's counts follow `referred`, but the tabs count every partner in each status
    // (PM decision, MOTHER-135): a filtered list asks for the counts once more, unfiltered.
    // One row is the least the route sends; a failure leaves the tabs as they were.
    if (referred !== null) {
      listPartners(fetcher, { limit: 1 }).then(
        (all) => live && setCounts(all.counts),
        () => undefined,
      );
    }
    return () => {
      live = false;
    };
  }, [fetcher, tab, q, referred, sort, offset, attempt]);

  /**
   * Moves the list's address. Typing a search replaces the entry; choosing a tab, the filter
   * or the order adds one, so Back undoes it.
   */
  const go = useCallback(
    (next: Partial<PartnersListParams>) => {
      clearTimeout(pause.current);
      const search = listSearch({ tab, q, referred, sort, ...next });
      setParams(search, { replace: Object.keys(next).every((key) => key === 'q') });
    },
    [q, referred, setParams, sort, tab],
  );

  /**
   * The route sorts by referrals one way only, most first, so the header is a switch: a click
   * on an unsorted list sorts it, and a click on the sorted one (which ListView sends as
   * ascending) returns to newest first. A phone's Sort menu offers "Referrals ↓", which sorts,
   * and "Referrals ↑", which acts as the same switch.
   */
  const onSortChange = useCallback(
    (next: { key: string; direction: 'asc' | 'desc' }) => {
      if (next.key !== REFERRALS_COLUMN) return;
      const sorted = next.direction === 'desc' || sort === null;
      go({ sort: sorted ? PARTNER_SORT_REFERRALS : null });
    },
    [go, sort],
  );

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      clearTimeout(pause.current);
      pause.current = setTimeout(() => {
        if (value.trim() !== q) go({ q: value });
      }, SEARCH_PAUSE_MS);
    },
    [go, q],
  );

  const list = load.state === 'ready' ? load.list : null;
  const listAddress = `${SETUP_ROUTES.partners}${params.toString() === '' ? '' : `?${params.toString()}`}`;
  const emptyTitle =
    referred !== null ? NO_FILTER_MATCH : q !== '' ? `No partners match "${q}".` : (PARTNER_TABS.find((t) => t.key === tab)?.empty ?? 'No partners.');
  const tabs = useMemo(
    () =>
      PARTNER_TABS.map((t) => {
        const n = tabCount(t.key, counts);
        return (
          <Tab key={t.key} value={t.key}>
            {t.label}
            {n === null ? null : (
              <>
                {' '}
                <Badge size="sm" variant="soft" tone="neutral">
                  {n}
                </Badge>
              </>
            )}
          </Tab>
        );
      }),
    [counts],
  );

  /**
   * Checks what the dialog's own required check cannot (a plausible email), then invites.
   * A refusal rejects, so the dialog stays open and shows it. On 201 the new partner's
   * page opens, fetching them fresh, with the notice; the list fetches again on return.
   */
  const onInvite = async (values: Record<string, string | null>): Promise<void> => {
    const invite = { email: values.email ?? '', firstName: values.firstName ?? '', lastName: values.lastName ?? '', accountId: values.accountId ?? '' };
    const problem = Object.values(validateInvite(invite))[0];
    if (problem) throw new Error(problem);
    const { partnerUser } = await invitePartner(fetcher, invite);
    setInviting(false);
    navigate(partnerPath(partnerUser.id), { state: { partnersList: listAddress, notice: `Invited ${partnerUser.email || invite.email.trim()}.` } satisfies FromList });
  };

  return (
    <PageContent as="section" width="wide">
      <Stack>
        <PageHeader
          className="border-0 bg-transparent px-0 pt-0 pb-0"
          eyebrow="Setup"
          title="Partners"
          subtitle="Partner applications: who applied, and where each stands."
          actions={<Button onClick={() => setInviting(true)}>Invite Partner</Button>}
        />
        <RecordFormDialog open={inviting} onOpenChange={setInviting} title="Invite Partner" fields={fieldsForInvite} submitLabel="Invite" onSubmit={onInvite} />
        <Tabs value={tab} onValueChange={(value) => isTab(value) && go({ tab: value })}>
          {/* Five tabs and their counts are wider than a phone: they wrap rather than scroll. */}
          <TabList aria-label="Partner Status" className="flex-wrap">
            {tabs}
          </TabList>
        </Tabs>
        <ListView
          variant="embedded"
          objectLabel="Partners"
          objectIcon={<Handshake />}
          columns={COLUMNS}
          columnPicker={false}
          selectable={false}
          rows={list?.partnerUsers ?? []}
          getRowId={(row) => row.id}
          object="partners"
          recordHref={(_object, id) => partnerPath(id)}
          onNavigate={(href) => navigate(href, { state: { partnersList: listAddress } satisfies FromList })}
          mode="server"
          search={searchText}
          onSearchChange={onSearchChange}
          onSearchSubmit={(value) => go({ q: value })}
          filters={[REFERRED_FILTER]}
          filterValues={referred === null ? {} : { referred }}
          onFilterChange={(_key, value) => go({ referred: isReferred(value) ? value : null })}
          {...(sort === null ? {} : { sort: { key: REFERRALS_COLUMN, direction: 'desc' as const } })}
          onSortChange={onSortChange}
          pagination="pages"
          page={Math.floor(offset / PARTNERS_PAGE_SIZE) + 1}
          pageSize={PARTNERS_PAGE_SIZE}
          total={list?.total ?? 0}
          onPageChange={(page) => setPaging({ key: listKey, offset: (page - 1) * PARTNERS_PAGE_SIZE })}
          onRefresh={() => setAttempt((n) => n + 1)}
          loading={load.state === 'loading'}
          {...(load.state === 'error' ? { error: load.message } : {})}
          empty={<EmptyState icon={<Handshake />} title={emptyTitle} />}
        />
      </Stack>
    </PageContent>
  );
}

/* ── /setup/partners/:id ───────────────────────────────────────── */

/** The buttons each status shows on the detail page, in order (PM decision 1). */
export const PARTNER_ACTIONS: Record<PartnerStatus, readonly PartnerAction[]> = {
  pending: ['verify', 'decline'],
  verified: ['deactivate'],
  declined: ['verify'],
  deactivated: ['verify'],
};

/** Each action's button and confirm label, its busy label, and its success notice. */
const ACTION_TEXT: Record<PartnerAction, { verb: string; busy: string; done: (name: string) => string }> = {
  verify: { verb: 'Verify', busy: 'Verifying…', done: (name) => `Verified ${name}.` },
  decline: { verb: 'Decline', busy: 'Declining…', done: (name) => `Declined ${name}.` },
  deactivate: { verb: 'Deactivate', busy: 'Deactivating…', done: (name) => `Deactivated ${name}.` },
};

export const actionsFor = (status: string): readonly PartnerAction[] =>
  (PARTNER_STATUSES as readonly string[]).includes(status) ? PARTNER_ACTIONS[status as PartnerStatus] : [];

/** The decline note's counter, e.g. `120 / 2,000`. */
const noteCount = (note: string): string => `${note.length.toLocaleString('en-US')} / ${NOTE_MAX.toLocaleString('en-US')}`;

type DetailLoad = { state: 'loading' } | { state: 'error'; message: string } | { state: 'missing'; message: string } | { state: 'ready'; partner: PartnerDetail };

/** The list's address the history entry carries, or the plain list when it carries none. */
function backAddress(state: unknown): string {
  const from = (state as FromList | null)?.partnersList;
  return typeof from === 'string' && (from === SETUP_ROUTES.partners || from.startsWith(`${SETUP_ROUTES.partners}?`)) ? from : SETUP_ROUTES.partners;
}

/** The Change Olytic Contact form's one field: who, by email. */
const OLYTIC_CONTACT_FIELD = 'olyticContactEmail';

/**
 * The Olytic Contact row's value: the person's name with their email beneath (`Not assigned`
 * for nobody), and the Change action beside it when the page offers one.
 */
function olyticContactValue(p: PartnerDetail, onChange?: () => void): React.ReactNode {
  const who = p.olyticContact ? (
    <Stack gap="none" className="min-w-0">
      <Text as="span">{p.olyticContact.name}</Text>
      <Text as="span" size="sm" tone="muted" className="break-all">
        {p.olyticContact.email}
      </Text>
    </Stack>
  ) : (
    <Text as="span">Not assigned</Text>
  );
  if (!onChange) return who;
  return (
    <Inline gap="sm" align="start" wrap>
      {who}
      <Button size="sm" variant="outline" className="shrink-0" onClick={onChange} aria-label="Change Olytic Contact">
        Change
      </Button>
    </Inline>
  );
}

/** What the page says under the Referral Rate (PM decision on MOTHER-144; Cameron's card on OLYAA-118). */
export const REFERRAL_RATE_HELP = 'Applies to referrals submitted after the change. The partner is not notified.';

/** The Change Referral Rate form's one field. */
const REFERRAL_RATE_FIELD = 'referralRate';

/**
 * The Referral Rate row's value: the rate as a percent (`Not set` for none), the Change action
 * beside it when the page offers one, and what a change does beneath.
 */
function referralRateValue(p: PartnerDetail, onChange?: () => void): React.ReactNode {
  const rate = <Text as="span">{p.referralRate === null ? 'Not set' : formatReferralRate(p.referralRate)}</Text>;
  return (
    <Stack gap="xs" className="min-w-0">
      {onChange ? (
        <Inline gap="sm" align="center" wrap>
          {rate}
          <Button size="sm" variant="outline" className="shrink-0" onClick={onChange} aria-label="Change Referral Rate">
            Change
          </Button>
        </Inline>
      ) : (
        rate
      )}
      <Text as="span" size="sm" tone="muted">
        {REFERRAL_RATE_HELP}
      </Text>
    </Stack>
  );
}

/** The Rate History's columns: newest first, as the server sends them. */
const RATE_HISTORY_COLUMNS: ColumnDef<ReferralRateChange>[] = [
  { key: 'changedAt', label: 'Changed', nowrap: true, get: (row) => row.changedAt, render: (row) => formatDateTime(row.changedAt) },
  { key: 'changedBy', label: 'Changed By', get: (row) => row.changedBy ?? 'Unknown' },
  { key: 'oldRate', label: 'Old Rate', align: 'right', nowrap: true, get: (row) => row.oldRate, render: (row) => formatReferralRate(row.oldRate) },
  { key: 'newRate', label: 'New Rate', align: 'right', nowrap: true, get: (row) => row.newRate, render: (row) => formatReferralRate(row.newRate) },
];

type HistoryLoad = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; changes: ReferralRateChange[] };

/** The Rate History section (MOTHER-144): every change of the partner's rate, newest first. Admins only. */
function RateHistory({ load, onRetry }: { load: HistoryLoad; onRetry: () => void }): JSX.Element {
  return (
    <Card>
      <CardHeader title="Rate History" icon={<History />} headingLevel={2} />
      <CardBody>
        {load.state === 'loading' ? <Spinner label="Loading rate history…" /> : null}
        {load.state === 'error' ? <ErrorState title="Could not load the rate history" description={load.message} onRetry={onRetry} /> : null}
        {load.state === 'ready' ? (
          <DataTable<ReferralRateChange>
            aria-label="Rate History"
            columns={RATE_HISTORY_COLUMNS}
            rows={load.changes}
            getRowId={(row) => `${row.changedAt}\n${row.oldRate ?? ''}\n${row.newRate ?? ''}`}
            mode="server"
            fit
            empty={<EmptyState icon={<History />} title="No rate changes yet" />}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Every field the detail route answers, read-only but for the Olytic Contact's Change action
 * (`onChangeContact`) and the Referral Rate's (`onChangeRate`, MOTHER-144).
 */
export function partnerFields(
  p: PartnerDetail,
  onChangeContact?: () => void,
  onChangeRate?: () => void,
): { key: string; label: string; value: React.ReactNode; wide?: boolean }[] {
  const link = (object: 'accounts' | 'contacts', id: string | null, text: string) =>
    id === null ? (
      'Not linked'
    ) : (
      <TextLink asChild>
        <Link to={recordPath(object, id)}>{text}</Link>
      </TextLink>
    );
  return [
    { key: 'name', label: 'Name', value: fullName(p) },
    { key: 'email', label: 'Email', value: p.email },
    { key: 'companyName', label: 'Company', value: p.companyName },
    { key: 'companyWebsite', label: 'Company Website', value: p.companyWebsite ?? '' },
    { key: 'phone', label: 'Phone', value: p.phone ?? '' },
    { key: 'heardAbout', label: 'How They Heard of Olytic', value: p.heardAbout ?? '' },
    { key: 'status', label: 'Status', value: <StatusBadge status={p.status} /> },
    { key: 'referralRate', label: 'Referral Rate', value: referralRateValue(p, onChangeRate) },
    { key: 'olyticContact', label: 'Olytic Contact', value: olyticContactValue(p, onChangeContact) },
    { key: 'createdAt', label: 'Applied', value: formatDate(p.createdAt) },
    { key: 'decidedAt', label: 'Decided', value: formatDate(p.decidedAt) },
    { key: 'lastSignedInAt', label: 'Last Sign-In', value: formatDateTime(p.lastSignedInAt) },
    { key: 'account', label: 'Account', value: link('accounts', p.accountId, 'Open Account') },
    { key: 'contact', label: 'Contact', value: link('contacts', p.contactId, 'Open Contact') },
    { key: 'partnershipVision', label: 'Partnership Vision', value: p.partnershipVision ?? '', wide: true },
    { key: 'decisionNote', label: 'Decision Note', value: p.decisionNote ?? '', wide: true },
  ];
}

/**
 * A partner's page. `role` is the signed-in person's (`/api/me`): only an Admin may change the
 * referral rate or read its history (Cameron's card on OLYAA-118), so anyone else sees neither,
 * and the history is not asked for.
 */
export function SetupPartnerPage({ fetcher, role }: { fetcher: Fetch; role?: string | undefined }): JSX.Element {
  const isAdmin = role === 'admin';
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const back = backAddress(location.state);
  const [load, setLoad] = useState<DetailLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState<string | null>(() => {
    const carried = (location.state as FromList | null)?.notice;
    return typeof carried === 'string' && carried !== '' ? carried : null;
  });
  // The open dialog, whether its call is running, and the server's refusal to show in it.
  const [dialog, setDialog] = useState<PartnerAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // The Change Olytic Contact dialog (MOTHER-137): its own form, apart from the status actions.
  const [changingContact, setChangingContact] = useState(false);
  const loadPeople = useCallback((query: string) => searchPeople(fetcher, query), [fetcher]);
  // The Change Referral Rate dialog and the Rate History (MOTHER-144), an Admin's only.
  const [changingRate, setChangingRate] = useState(false);
  const [history, setHistory] = useState<HistoryLoad>({ state: 'loading' });
  const [historyAttempt, setHistoryAttempt] = useState(0);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let live = true;
    setHistory({ state: 'loading' });
    listReferralRateHistory(fetcher, id).then(
      (changes) => live && setHistory({ state: 'ready', changes }),
      (error: unknown) => live && setHistory({ state: 'error', message: errorText(error) }),
    );
    return () => {
      live = false;
    };
  }, [fetcher, id, isAdmin, historyAttempt]);

  // A notice the history entry carried shows once: a reload or a return here does not repeat it.
  useEffect(() => {
    const state = location.state as FromList | null;
    if (state?.notice === undefined) return;
    const { notice: _shown, ...rest } = state;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: rest });
  }, [location, navigate]);

  useEffect(() => {
    let live = true;
    setLoad({ state: 'loading' });
    getPartner(fetcher, id).then(
      (result) => live && setLoad(result.found ? { state: 'ready', partner: result.partner } : { state: 'missing', message: result.message }),
      (error: unknown) => live && setLoad({ state: 'error', message: errorText(error) }),
    );
    return () => {
      live = false;
    };
  }, [fetcher, id, attempt]);

  const partner = load.state === 'ready' ? load.partner : null;
  const name = partner ? fullName(partner) || partner.email : 'Partner';
  const contact = partner?.olyticContact ?? null;
  // The form opens on the current contact, so its lookup offers Clear; with nobody assigned
  // the field is required, which leaves Clear out (the lookup offers it only on an optional field).
  const contactFields = useMemo<RecordFormField[]>(
    () => [{ key: OLYTIC_CONTACT_FIELD, label: 'Olytic Contact', required: contact === null, editor: { kind: 'lookup', loadOptions: loadPeople } }],
    [contact, loadPeople],
  );
  const contactValues = useMemo(() => (contact ? { [OLYTIC_CONTACT_FIELD]: contact.email } : {}), [contact]);
  const contactLabels = useMemo(() => (contact ? { [OLYTIC_CONTACT_FIELD]: contact.name } : {}), [contact]);
  const rate = partner?.referralRate ?? null;
  const rateValues = useMemo(() => (rate === null ? {} : { [REFERRAL_RATE_FIELD]: String(rate) }), [rate]);
  // Only a verified partner's rate changes here (MOTHER-144 criterion 7: none for a pending or declined one).
  const canChangeRate = isAdmin && partner?.status === 'verified';
  useDocumentTitle(name);
  const backLink = (
    <TextLink asChild>
      <Link to={back}>Back to Partners</Link>
    </TextLink>
  );

  const open = (action: PartnerAction) => {
    setRefusal(null);
    setNote('');
    setDialog(action);
  };
  // Cancel, Escape and the close button land here; ConfirmDialog ignores them while busy.
  const close = (isOpen: boolean) => {
    if (isOpen || busy) return;
    setDialog(null);
    setRefusal(null);
  };

  /**
   * Calls the action's route. A success closes the dialog, shows the notice and fetches the
   * partner again (never patched locally); a refusal keeps the dialog open with the
   * server's text.
   */
  const act = async (action: PartnerAction, call: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      await call();
      setDialog(null);
      setNotice(ACTION_TEXT[action].done(name));
      setAttempt((n) => n + 1);
    } catch (error) {
      setRefusal(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };
  const confirm = (action: 'verify' | 'deactivate') => {
    const call = action === 'verify' ? () => verifyPartner(fetcher, id) : () => deactivatePartner(fetcher, id);
    act(action, call).catch(() => undefined);
  };
  // The decline form's own dialog shows a rejection (under its title) and stays open.
  const onDecline = async (values: Record<string, string | null>): Promise<void> => {
    const text = (values.note ?? '').trim();
    if (text.length > NOTE_MAX) throw new Error(NOTE_TOO_LONG);
    await act('decline', () => declinePartner(fetcher, id, text));
  };

  /**
   * Sends the chosen person's email, or null after Clear. A refusal rejects, so the dialog
   * stays open with the server's text; a success closes it and shows the contact the server
   * answered with, without fetching the partner again.
   */
  const onChangeContact = async (values: Record<string, string | null>): Promise<void> => {
    const { olyticContact } = await setOlyticContact(fetcher, id, values[OLYTIC_CONTACT_FIELD] ?? null);
    setChangingContact(false);
    setLoad((current) => (current.state === 'ready' ? { state: 'ready', partner: { ...current.partner, olyticContact } } : current));
  };

  /**
   * Checks the rate before sending it (0 to 25, at most 2 decimals), then PATCHes exactly
   * `{ referralRate }`. A refusal rejects, so the dialog stays open with the server's text and
   * the shown rate is unchanged; a success closes it, shows the rate the server answered with
   * without fetching the partner again, and fetches the history again for its new row.
   */
  const onChangeRate = async (values: Record<string, string | null>): Promise<void> => {
    const next = parseReferralRate(values[REFERRAL_RATE_FIELD] ?? '');
    if (next === null) throw new Error(REFERRAL_RATE_INVALID);
    const { referralRate } = await setReferralRate(fetcher, id, next);
    setChangingRate(false);
    setLoad((current) => (current.state === 'ready' ? { state: 'ready', partner: { ...current.partner, referralRate } } : current));
    setHistoryAttempt((n) => n + 1);
  };

  const refusalMessage = refusal ? (
    <InlineMessage tone="error" role="alert">
      {refusal}
    </InlineMessage>
  ) : null;
  const declineFields: RecordFormField[] = [
    {
      key: 'note',
      label: 'Note',
      multiline: true,
      editor: { kind: 'text', multiline: true },
      help: noteCount(note),
      render: ({ value, onChange, disabled, label }) => (
        <Textarea
          aria-label={label}
          dictation
          rows={6}
          value={value}
          readOnly={disabled}
          onChange={(event) => {
            setNote(event.target.value);
            onChange(event.target.value);
          }}
        />
      ),
    },
  ];
  const actions = partner
    ? actionsFor(partner.status).map((action) => (
        <Button key={action} variant={action === 'verify' ? 'brand' : 'neutral'} onClick={() => open(action)}>
          {ACTION_TEXT[action].verb}
        </Button>
      ))
    : [];

  return (
    <PageContent as="section" width="wide">
      <Stack>
        <PageHeader
          className="border-0 bg-transparent px-0 pt-0 pb-0"
          breadcrumbs={backLink}
          eyebrow="Partner"
          title={name}
          {...(partner?.companyName ? { subtitle: partner.companyName } : {})}
          {...(actions.length > 0 ? { actions: <>{actions}</> } : {})}
        />
        {notice ? (
          <InlineMessage tone="success" role="status">
            {notice}
          </InlineMessage>
        ) : null}
        {load.state === 'loading' ? <Spinner label="Loading partner…" /> : null}
        {load.state === 'missing' ? <EmptyState icon={<Handshake />} title={load.message} /> : null}
        {load.state === 'error' ? <ErrorState title="Could not load this partner" description={load.message} onRetry={() => setAttempt((n) => n + 1)} /> : null}
        {partner ? (
          <Card>
            <CardBody>
              <DetailPanel fields={partnerFields(partner, () => setChangingContact(true), canChangeRate ? () => setChangingRate(true) : undefined)} />
            </CardBody>
          </Card>
        ) : null}
        {partner && isAdmin ? <RateHistory load={history} onRetry={() => setHistoryAttempt((n) => n + 1)} /> : null}
        {/* Fetched beside the application, on its own: it stays while the partner is fetched again after an action. */}
        {load.state === 'ready' || load.state === 'loading' ? <PartnerReferrals key={id} fetcher={fetcher} partnerId={id} partnerStatus={partner?.status ?? null} /> : null}
        <ConfirmDialog
          open={dialog === 'verify'}
          onOpenChange={close}
          title={`Verify ${name} as a partner?`}
          description={
            <Stack gap="sm">
              <span>Mothership matches an account and contact from their application, or creates them.</span>
              {refusalMessage}
            </Stack>
          }
          confirmLabel={busy ? ACTION_TEXT.verify.busy : ACTION_TEXT.verify.verb}
          busy={busy}
          onConfirm={() => confirm('verify')}
        />
        <ConfirmDialog
          open={dialog === 'deactivate'}
          onOpenChange={close}
          title={`Deactivate ${name}?`}
          description={
            <Stack gap="sm">
              <span>They will no longer be able to sign in to the partner portal.</span>
              {refusalMessage}
            </Stack>
          }
          tone="danger"
          confirmLabel={busy ? ACTION_TEXT.deactivate.busy : ACTION_TEXT.deactivate.verb}
          busy={busy}
          onConfirm={() => confirm('deactivate')}
        />
        <RecordFormDialog open={dialog === 'decline'} onOpenChange={close} title={`Decline ${name}?`} fields={declineFields} submitLabel="Decline" onSubmit={onDecline} />
        <RecordFormDialog
          open={changingContact}
          onOpenChange={setChangingContact}
          title="Change Olytic Contact"
          fields={contactFields}
          initialValues={contactValues}
          initialLabels={contactLabels}
          submitLabel="Save"
          onSubmit={onChangeContact}
        />
        <RecordFormDialog
          open={changingRate}
          onOpenChange={setChangingRate}
          title="Change Referral Rate"
          fields={[
            {
              key: REFERRAL_RATE_FIELD,
              label: 'Referral Rate',
              required: true,
              // A text editor under a number input: the dialog's own number check would answer a
              // bad rate with a generic message, so onChangeRate checks it and says what is allowed.
              editor: { kind: 'text' },
              help: REFERRAL_RATE_HELP,
              render: ({ value, onChange, disabled, label }) => (
                <Input
                  aria-label={label}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={REFERRAL_RATE_MAX}
                  step={0.01}
                  fit="compact"
                  endSlot="%"
                  value={value}
                  readOnly={disabled}
                  onChange={(event) => onChange(event.target.value)}
                />
              ),
            },
          ]}
          initialValues={rateValues}
          submitLabel="Save"
          onSubmit={onChangeRate}
        />
      </Stack>
    </PageContent>
  );
}
